import type { CostBreakdown } from './api';
import { CostRow } from './cost-row';

/**
 * As três visões da tabela de custos. Cada uma sabe o próprio rótulo, o cabeçalho
 * da primeira coluna e como virar linhas — então acrescentar uma visão nova não
 * mexe no template, e a aba viaja na URL pelo `slug`.
 */
export class CostsTab {
  static readonly CLIENT = new CostsTab('client', 'Por cliente', 'Cliente', (data) =>
    data.byClient.map((client) =>
      CostRow.fromClient(client).withChildren(
        data.byWorkflow
          .filter((workflow) => workflow.tenantId === client.tenantId)
          .map(CostRow.fromWorkflow),
      ),
    ),
  );
  static readonly WORKFLOW = new CostsTab('workflow', 'Por workflow', 'Workflow', (data) =>
    data.byWorkflow.map(CostRow.fromWorkflow),
  );
  static readonly MODEL = new CostsTab('model', 'Por modelo', 'Provedor / Modelo', (data) =>
    data.byModel.map(CostRow.fromModel),
  );

  private static readonly BY_SLUG: Record<string, CostsTab> = {
    client: CostsTab.CLIENT,
    workflow: CostsTab.WORKFLOW,
    model: CostsTab.MODEL,
  };

  private constructor(
    readonly slug: string,
    readonly label: string,
    readonly firstColumn: string,
    private readonly extract: (data: CostBreakdown) => CostRow[],
  ) {}

  static all(): CostsTab[] {
    return [CostsTab.CLIENT, CostsTab.WORKFLOW, CostsTab.MODEL];
  }

  /** Slug desconhecido cai em "por cliente", que é a visão de abertura. */
  static fromQuery(slug: string | null): CostsTab {
    return CostsTab.BY_SLUG[slug ?? ''] ?? CostsTab.CLIENT;
  }

  rowsOf(data: CostBreakdown): CostRow[] {
    const rows = this.extract(data);
    const largestBrl = Math.max(0, ...rows.map((row) => row.totalBrl));
    return rows.map((row) => row.withScale({ periodBrl: data.total.brl, largestBrl }));
  }

  is(other: CostsTab): boolean {
    return this.slug === other.slug;
  }
}
